// EP-135 Tier 3.6 — ETDA ขมธอ. 26-2564 audit log page. Staff+ read
// the per-resolution ballot export; admin+ use this for the chair +
// secretary walk-through before signing the certificate (Tier 4).
//
// §4a + D-2 — secret resolutions hide attendee_id + customer_name +
// recorded_by_id at the SERIALIZER. The receipt_hash + aggregate
// choice weight remain visible so a voter can claim "I cast X" and
// the chair can verify "X was the aggregated choice at this receipt"
// without identifying the voter.
import { useParams, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, FileSearch, Lock, Download } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Card, Button, Badge, Skeleton, EmptyState } from '@/components/ui'
import { useMeetingAuditLog } from '@/hooks/useGovernance'
import { useDateTimeFormatter } from '@/hooks/useDateFormatter'

export function AuditLogPage() {
  const { t } = useTranslation('governance')
  const { t: tc } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const meetingId = Number(id)
  const formatDateTime = useDateTimeFormatter()

  const { data: payload, isLoading, error } = useMeetingAuditLog(meetingId, !!meetingId)

  const handleDownload = () => {
    if (!payload) return
    const blob = new Blob([ JSON.stringify(payload, null, 2) ], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `meeting-${meetingId}-audit-log.json`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-1/2" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (error || !payload) {
    return (
      <div className="space-y-6">
        <EmptyState
          icon={<FileSearch className="w-10 h-10" />}
          title={tc('errors.something_went_wrong')}
          description={tc('errors.something_went_wrong')}
          action={<Button onClick={() => navigate(`/meetings/${meetingId}`)}>{t('meetings.actions.back_to_meetings')}</Button>}
        />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: t('meetings.title'), href: '/meetings' },
          { label: payload.meeting.title, href: `/meetings/${meetingId}` },
          { label: t('votes.audit_log.title') },
        ]}
      />

      <PageHeader
        title={t('votes.audit_log.title')}
        description={payload.meeting.title}
        titleAction={
          <Badge variant={payload.meeting.electronic_voting_permitted ? 'success' : 'count'}>
            {payload.meeting.electronic_voting_permitted
              ? t('votes.audit_log.electronic_voting_permitted')
              : '—'}
          </Badge>
        }
        overflowActions={[
          {
            key: 'download',
            label: t('votes.audit_log.download'),
            icon: Download,
            onClick: handleDownload,
            testId: 'btn-download-audit-log',
          },
        ]}
        primaryAction={undefined}
      />

      <p className="text-sm text-base-content/70">{t('votes.audit_log.description')}</p>

      {payload.resolutions.length === 0 ? (
        <EmptyState
          icon={<FileSearch className="w-10 h-10" />}
          title={t('votes.audit_log.no_ballots')}
          description={t('votes.audit_log.no_ballots')}
        />
      ) : (
        <div className="space-y-4">
          {payload.resolutions.map((r) => (
            <Card
              key={r.id}
              title={`#${r.seq} ${r.title}`}
              description={r.secret ? t('votes.messages.secret_no_identity') : undefined}
              actions={
                <Badge variant="count">{t(`resolutions.statuses.${r.status}`)}</Badge>
              }
            >
              {r.ballots.length === 0 ? (
                <p className="text-xs text-base-content/60">
                  {t('votes.audit_log.no_ballots')}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>{t('votes.labels.attendee')}</th>
                        <th>{t('votes.labels.choice')}</th>
                        <th>{t('votes.labels.weight')}</th>
                        <th>{t('votes.labels.cast_at')}</th>
                        <th>{t('votes.labels.cast_via')}</th>
                        <th>{t('votes.labels.identity_level')}</th>
                        <th>{t('votes.labels.receipt_hash')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {r.ballots.map((b) => (
                        <tr key={b.id} className="hover:bg-base-200">
                          <td>
                              {b.attendee_id !== null ? b.customer_name : (
                                <span className="inline-flex items-center gap-1 text-base-content/60">
                                  <Lock className="w-3 h-3" /> —
                                </span>
                              )}
                            </td>
                          <td>
                            <Badge variant="count">{t(`votes.choices.${b.choice}`)}</Badge>
                          </td>
                          <td className="font-mono">{b.weight_at_cast}</td>
                          <td className="text-xs">{formatDateTime(b.cast_at)}</td>
                          <td className="text-xs">{t(`votes.cast_via.${b.cast_via}`)}</td>
                          <td className="text-xs">{t(`votes.identity_levels.${b.identity_level}`)}</td>
                          <td className="font-mono text-xs">{b.receipt_hash.slice(0, 12)}…</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <Button variant="ghost" leftIcon={<ArrowLeft className="w-4 h-4" />} onClick={() => navigate(`/meetings/${meetingId}`)}>
        {t('meetings.actions.back_to_meetings')}
      </Button>
    </div>
  )
}